import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

// Create a singleton adapter instance
const adapter = new AdapterMoment();

/**
 * Stable date utilities object. Must be a singleton — returning a new object from
 * useUtils on every render breaks useMemo/useEffect deps (e.g. form reset loops).
 */
const dateUtils = {
  ...adapter,
  // Additional utility method for creating dates (but don't override adapter.date if it exists)
  date: (value?: any): Moment => {
    if (!value) return moment();
    return moment.isMoment(value) ? value : moment(value);
  },
  parse: (value: string, format: string): Moment => moment(value, format),
  format: (date: Moment | Date | string | null | undefined, format: string | any): string => {
    if (!date) return '';
    return moment(date).format(format as string);
  },
  isValid: (date: any): boolean => {
    if (!date) return false;
    return moment(date).isValid();
  },
  isEqual: (date1: any, date2: any) => moment(date1).isSame(moment(date2)),
  isSameDay: (date1: any, date2: any) => moment(date1).isSame(moment(date2), 'day'),
  isAfter: (date1: any, date2: any) => moment(date1).isAfter(moment(date2)),
  isBefore: (date1: any, date2: any) => moment(date1).isBefore(moment(date2)),
  startOfDay: (date: any) => moment(date).startOf('day'),
  endOfDay: (date: any) => moment(date).endOf('day'),
  addDays: (date: any, count: number) => moment(date).add(count, 'days'),
  startOfMonth: (date: any) => moment(date).startOf('month'),
  endOfMonth: (date: any) => moment(date).endOf('month'),
  getYear: (date: any) => moment(date).year(),
  setYear: (date: any, year: number) => moment(date).year(year),
  getMonth: (date: any) => moment(date).month(),
  setMonth: (date: any, month: number) => moment(date).month(month),
  getDate: (date: any) => moment(date).date(),
  setDate: (date: any, dayOfMonth: number) => moment(date).date(dayOfMonth),
  getHours: (date: any) => moment(date).hours(),
  setHours: (date: any, hours: number) => moment(date).hours(hours),
  getMinutes: (date: any) => moment(date).minutes(),
  setMinutes: (date: any, minutes: number) => moment(date).minutes(minutes),
  getSeconds: (date: any) => moment(date).seconds(),
  setSeconds: (date: any, seconds: number) => moment(date).seconds(seconds),
  getDaysInMonth: (date: any) => moment(date).daysInMonth(),
  toJsDate: (date: any) => moment(date).toDate(),
  // Custom utility methods
  getHourText: (date: any, ampm?: boolean): string => {
    const hours = moment(date).hours();
    if (ampm) {
      const hour12 = hours > 12 ? hours - 12 : hours || 12;
      return hour12.toString().padStart(2, '0');
    }
    return hours.toString().padStart(2, '0');
  },
  getMinuteText: (date: any) => moment(date).minutes().toString().padStart(2, '0'),
  yearFormat: 'YYYY',
  getDateTimePickerHeaderText: (date: any) => moment(date).format('ddd, MMM D'),
};

/**
 * Hook to get date utilities similar to useUtils from @material-ui/pickers
 * This provides compatibility for existing code using useUtils
 */
export const useUtils = () => dateUtils;

/**
 * Get the date adapter instance directly
 */
export const getDateAdapter = () => adapter;

/**
 * Format a date using moment
 */
export const formatDate = (date: any, format: string) => {
  if (!date) return '';
  return moment(date).format(format);
};

/**
 * Parse a date string
 */
export const parseDate = (dateString: string, format?: string) =>
  format ? moment(dateString, format) : moment(dateString);
